// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';
import { type LargeStepsSignedUrl } from '@canvas/model/steps';
import {
  RecoverableNetworkError,
  UnrecoverableNetworkError,
  retryWithBackoff,
} from '@canvas/retry';
import { type TokenManager } from '@canvas/token-manager';

import { BoardDocumentMissingError } from './errors';
import { fetchWithTimeout } from './fetch-with-timeout';

const FIVE_MINUTES = 5 * 60 * 1000;

export async function fetchLargeStep(signedDownloadUrl: string | LargeStepsSignedUrl) {
  const response = await retryWithBackoff({
    callback: async () => {
      let url: string;
      const headers: { [key: string]: string } = {};

      if (typeof signedDownloadUrl === 'string') {
        url = signedDownloadUrl;
      } else {
        url = signedDownloadUrl.presignedUrl;
        if (signedDownloadUrl.shouldReadFromOS && signedDownloadUrl.accessToken) {
          headers['Authorization'] = signedDownloadUrl.accessToken;
        }
      }
      const response = await fetchWithTimeout(
        url,
        { headers },
        { timeout: FIVE_MINUTES }
      );
      if (!response.ok) {
        if (response.status === 408) {
          throw new RecoverableNetworkError(`Timed out attempting to fetch large step`);
        } else if (response.status >= 500) {
          throw new RecoverableNetworkError(
            `Received a server error status code ${response.status} from S3`
          );
        } else {
          throw new UnrecoverableNetworkError(
            `Unrecoverable error status code ${response.status} from S3`
          );
        }
      }
      return response;
    },
    maxRetries: Number.POSITIVE_INFINITY,
    retryTimeMs: 500,
    shouldRetry: (error) => {
      return error instanceof RecoverableNetworkError;
    },
  });
  return response;
}

export interface PersistLargeStepOptions {
  serverUrl: string;
  documentId: string;
  step: Uint8Array<ArrayBuffer>;
  headers?: Record<string, string>;
  tokenManager: TokenManager;
}

export const persistLargeStep = async ({
  serverUrl,
  documentId,
  step,
  headers,
  tokenManager,
}: PersistLargeStepOptions) => {
  const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);
  const opts: RequestInit = {
    headers: {
      'content-type': 'application/octet-stream',
      'x-access-token': token.accessToken,
    },
  };

  const { sessionId } = currentSessionId;
  const url = sessionId
    ? `${serverUrl}/api/_internal/collab/${encodeURIComponent(
        documentId
      )}/large-step?sessionId=${sessionId}`
    : `${serverUrl}/api/_internal/collab/${encodeURIComponent(documentId)}/large-step`;

  const result = await fetchWithTimeout(
    url,
    {
      ...opts,
      method: 'POST',
      body: step,
      headers: {
        ...opts.headers,
        ...headers,
      },
    },
    { timeout: FIVE_MINUTES }
  );

  if (!result.ok) {
    if (result.status === 408) {
      throw new RecoverableNetworkError(
        `Timed out attempting to fetch document ${documentId}`
      );
    } else if (result.status === 404) {
      throw new BoardDocumentMissingError();
    } else if (result.status >= 500) {
      throw new RecoverableNetworkError(
        `Unable to persist large step for ${documentId} but can retry`
      );
    } else {
      throw new RecoverableNetworkError(
        `Unable to persist large step for document ${documentId}`
      );
    }
  }
};
